import { Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { collection, collectionData, collectionGroup, doc, docData, DocumentReference, Firestore, query, runTransaction, serverTimestamp, where } from '@angular/fire/firestore';

import { pluck, switchMap, map, retryWhen, concatMap, delay, shareReplay } from 'rxjs/operators';
import { of, Observable, combineLatest, throwError, iif } from 'rxjs';
import { ActionsService } from '../actions/actions.service';
import { UsersService } from '../users/users.service';
import { Assignment, FreshAssignment, AssignmentWithId, User } from '@innersight-os/shared/firestore-interfaces';
import { DocumentData } from '@firebase/firestore-types';

export interface AssignmentWithUser extends AssignmentWithId {
  user: User;
}

@Injectable({
  providedIn: 'root'
})
export class AssignmentsService {
  constructor(
    private actions: ActionsService,
    private auth: Auth,
    private db: Firestore,
    private users: UsersService,
  ) {}

  myId$ = authState(this.auth).pipe(pluck('uid'))

  // eslint-able-next-line @typescript-eslint/no-explicit-any
  allAssignments$: Observable<AssignmentWithId[]> = collectionData<AssignmentWithId>(collectionGroup(this.db, 'assignments') as any)

  myAssignments$: Observable<AssignmentWithId[]> = this.myId$.pipe(
    switchMap(uid => uid
      ? collectionData(
          query<AssignmentWithId, DocumentData>(
            // eslint-able-next-line @typescript-eslint/no-explicit-any
            collectionGroup(this.db, 'assignments') as any,
            where('userId', '==', uid),
          ),
          {idField: 'id'},
        ) as Observable<AssignmentWithId[]>
      : of(null) // no uid
    ),
    shareReplay({bufferSize: 1, refCount: true})
  )

  myAssignedOperationIds$: Observable<string[]> = this.myAssignments$.pipe(
    map(myAssignments => myAssignments.map(assignment => assignment.operationId)),
    retryWhen(errors=>errors.pipe(
      concatMap((err: Error, i) =>
        iif(() => {
            i<3 && console.log(`myAssignedOperationIds$ error ${err.message} retry #`,i)
            return i > 3
          },
          throwError(() => err),
          of(err).pipe(delay(2000))
    ))))
  )

  // eslint-able-next-line @typescript-eslint/consistent-indexed-object-style
  assignmentsByOperation$: Observable<{[opId: string]: Assignment[]}> = this.users.iCanAssign$.pipe(
    switchMap(iCanAssign =>
      (iCanAssign ? this.allAssignments$ : this.myAssignments$).pipe(
        map(as => as.reduce((acc, a) => ({
          ...acc,
          [a.operationId]: [...(acc[a.operationId] ?? []), a],
        }), {}))
      )
    ),
    shareReplay({bufferSize: 1, refCount: true})
  )

  getOperationAssignments(operationId: string): Observable<AssignmentWithId[]> {
    return collectionData<AssignmentWithId>(
      // eslint-able-next-line @typescript-eslint/no-explicit-any
      collection(this.db, 'operations', operationId, 'assignments') as any,
      {idField: 'id'},
    )
  }

  getOperationAssignmentsWithUsers(operationId: string): Observable<AssignmentWithUser[] | null> {
    return this.getOperationAssignments(operationId).pipe(
      switchMap(assignments =>
        assignments?.length > 0
          ? combineLatest(
              assignments.map(assignment =>
                this.users.getUser(assignment.userId).pipe(
                  map(user => ({...assignment, user}))
                )
              )
            )
          : of(null)
      )
    )
  }

  getMyAssignmentForOperation(operationId: string): Observable<AssignmentWithId> {
    return this.myId$.pipe(
      switchMap(uid => uid
        ? docData<AssignmentWithId>(
            doc(this.db, `operations/${operationId}/assignments/${uid}`) as DocumentReference<AssignmentWithId>,
            {idField: 'id'},
          )
        : of(null) // no uid
      ),
    )

  }

  async assignSegmenterToOperation(segmenterId: string, operationId: string) {
    try {
      return await runTransaction(this.db, async transaction => {
          // create new assignment doc for assigned segger
        const assignment: FreshAssignment = {
          userId: segmenterId,
          operationId,
          createdAt: serverTimestamp(),
          isAccepted: false,
        }
        const assignmentRef =  doc(this.db, 'operations', operationId, 'assignments', segmenterId)
        transaction.set(assignmentRef, assignment)

        // create the paired action doc
        this.actions.recordAction(segmenterId, operationId, {type: 'assigned'}, transaction)
      })
    } catch(err) {
      console.error('transaction failed', err);
    }
  }

  async unassignSegmenterFromOperation(segmenterId: string, operationId: string) {
    try {
      return await runTransaction(this.db, async transaction => {
        const assignmentRef = doc(this.db, 'operations', operationId, 'assignments', segmenterId)
        transaction.delete(assignmentRef)
        // create the paired action doc
        this.actions.recordAction(segmenterId, operationId, {type: 'unassigned'}, transaction)
      })
    } catch(err){
      console.error('transaction failed', err);
    }
  }

  async accept(operationId: string) {
    try {
      return runTransaction(this.db, async transaction => {
        const userId = this.auth.currentUser.uid
        const assignmentRef = doc(this.db, 'operations' ,operationId, 'assignments', userId)
        transaction.update(assignmentRef, {isAccepted: true} as Partial<Assignment>)
        this.actions.recordAction(undefined, operationId, {type: 'accepted'}, transaction)
      })
    } catch(err){
      console.error('transaction failed', err);
    }
  }
}
